import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";

import { accounts } from "../../api/endpoints/accounts";
import { tenants } from "../../api/endpoints/tenants";
import { sites } from "../../api/endpoints/sites";
import { allocations } from "../../api/endpoints/allocations";
import { allocationVlans } from "../../api/endpoints/allocationVlans";
import { qk } from "../../api/queryKeys";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { buildAllocationTree, type AllocNode, type OrgSite } from "./buildAllocationTree";

export function useAllocationTree(): { tree: AllocNode | null; isPending: boolean; error: unknown } {
  const accountsQ = useQuery({
    ...adminQueryOptions,
    queryKey: qk.accounts(),
    queryFn: () => accounts.list().then((r) => r.data),
  });
  const ledgerQ = useQuery({
    ...adminQueryOptions,
    queryKey: qk.allocationLedger(),
    queryFn: () => allocations.ledger().then((r) => r.data),
  });
  const templateQ = useQuery({
    ...adminQueryOptions,
    queryKey: qk.vlanTemplate(),
    queryFn: () => allocationVlans.vlanTemplate().then((r) => r.data),
  });

  const acctSlugs = (accountsQ.data ?? []).map((a) => a.slug);
  const tenantQs = useQueries({
    queries: acctSlugs.map((slug) => ({
      ...adminQueryOptions,
      queryKey: qk.tenants(slug),
      queryFn: () => tenants.list(slug).then((r) => r.data),
    })),
  });

  // flatten (account, tenant) pairs for the per-tenant site queries
  const tenantPairs: Array<{ account: string; tenant: string }> = [];
  acctSlugs.forEach((account, i) => {
    (tenantQs[i]?.data ?? []).forEach((t) => tenantPairs.push({ account, tenant: t.slug }));
  });
  const siteQs = useQueries({
    queries: tenantPairs.map((p) => ({
      ...adminQueryOptions,
      queryKey: qk.sites(p.account, p.tenant),
      queryFn: () => sites.list(p.account, p.tenant).then((r) => r.data),
    })),
  });

  const orgSites: OrgSite[] = [];
  tenantPairs.forEach((p, i) => {
    (siteQs[i]?.data ?? []).forEach((s) =>
      orgSites.push({ accountSlug: p.account, siteSlug: s.slug, displayName: s.display_name }));
  });

  const orgSitesKey = JSON.stringify(orgSites);
  const tree = useMemo(() => {
    if (!accountsQ.data || !ledgerQ.data || !templateQ.data) return null;
    return buildAllocationTree({
      accounts: accountsQ.data.map((a) => ({ slug: a.slug, display_name: a.display_name })),
      orgSites,
      ledger: ledgerQ.data,
      vlanTemplate: templateQ.data,
    });
    // orgSites derives from the dependent queries; recompute when their data settles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsQ.data, ledgerQ.data, templateQ.data, orgSitesKey]);

  // Aggregate the dependent org fan-out: a failed tenant/site query must surface
  // (otherwise the estate renders incomplete and silent).
  const depPending = tenantQs.some((q) => q.isPending) || siteQs.some((q) => q.isPending);
  const depError =
    tenantQs.find((q) => q.error)?.error ?? siteQs.find((q) => q.error)?.error ?? null;

  return {
    tree,
    isPending: accountsQ.isPending || ledgerQ.isPending || templateQ.isPending || depPending,
    error: accountsQ.error ?? ledgerQ.error ?? templateQ.error ?? depError,
  };
}
